import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BrandWordmark } from "@/components/layout/BrandWordmark";
import { TYCOONEX_X_URL } from "@/lib/api";

/**
 * AppShell 전 페이지 공용 풋터 (랜딩 `/` 은 자체 LandingFooter 사용).
 * 에디토리얼 구성: 브랜드 블록 + 링크 컬럼 2개 (Explore / Community) →
 * 헤어라인 → 법적 고지 행 (Terms · Privacy · Docs 링크 허브) →
 * 최하단 저대비 세리프 2행 워터마크 + 듀오톤 비서 선화 (Hermes 푸터 문법).
 */

/** GitBook 문서 URL — 확정되면 여기만 채우면 Docs가 실링크로 바뀐다. */
const DOCS_URL: string | null = null;

type FooterLink = {
  label: string;
  to?: string;
  href?: string;
  soon?: boolean;
};

const EXPLORE_LINKS: FooterLink[] = [
  { label: "Home", to: "/home" },
  { label: "Agents", to: "/agents" },
  { label: "Shop", to: "/shop" },
  { label: "Collection", to: "/profile/collection" },
];

const COMMUNITY_LINKS: FooterLink[] = [
  { label: "X (Twitter)", href: TYCOONEX_X_URL },
  { label: "Telegram", soon: true },
  { label: "Pioneer Event", href: "https://pioneer.tycoonex.com" },
];

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div className="flex flex-col gap-2.5">
      <p className="font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-ink-mute">
        {title}
      </p>
      <ul className="flex flex-col gap-2">
        {links.map((link) => (
          <li key={link.label}>
            <FooterLinkItem link={link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterLinkItem({ link }: { link: FooterLink }) {
  const className =
    "text-[13px] text-ink-dim transition-colors duration-200 hover:text-ink";
  if (link.to) {
    return (
      <Link to={link.to} className={className}>
        {link.label}
      </Link>
    );
  }
  if (link.href) {
    return (
      <a href={link.href} target="_blank" rel="noreferrer" className={className}>
        {link.label}
        <span aria-hidden className="ml-1 text-ink-mute">
          ↗
        </span>
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => toast("Coming soon")}
      className={`${className} inline-flex items-center gap-1.5 text-left`}
    >
      {link.label}
      <span className="rounded-full border border-line px-1.5 py-px font-mono text-[8px] font-medium uppercase tracking-[0.12em] text-ink-mute">
        Soon
      </span>
    </button>
  );
}

export function SiteFooter() {
  return (
    <footer className="relative z-10 mt-16 border-t border-line">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        {/* 브랜드 + 링크 컬럼 (Brand / Explore / Community) */}
        <div className="grid gap-10 py-12 sm:grid-cols-[1.4fr_1fr_1fr] sm:gap-8 sm:py-14">
          <div className="flex max-w-xs flex-col gap-4">
            <BrandWordmark imgClassName="h-5.5" />
            <p className="text-[13px] leading-relaxed text-ink-mute">
              Collect AI assistant cards, earn IP every day, and rank up for
              higher airdrop rewards.
            </p>
          </div>
          <FooterColumn title="Explore" links={EXPLORE_LINKS} />
          <FooterColumn title="Community" links={COMMUNITY_LINKS} />
        </div>

        {/* 법적 고지 행 — 링크 허브: Terms · Privacy · Docs (페이지 준비 전까지 Soon) */}
        <div className="flex flex-col gap-3 border-t border-line py-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-mute">
            © 2026 TycoonEX. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <FooterLinkItem link={{ label: "Terms", soon: true }} />
            <FooterLinkItem link={{ label: "Privacy", soon: true }} />
            <FooterLinkItem
              link={DOCS_URL ? { label: "Docs", href: DOCS_URL } : { label: "Docs", soon: true }}
            />
          </div>
        </div>
      </div>

      {/* 워터마크 피날레 (scroll-asset-prd §2 A4) — 저대비 세리프 1행, 정적·장식 전용. */}
      <div aria-hidden className="pointer-events-none relative select-none overflow-hidden">
        <p className="-mb-[0.18em] pt-10 font-serif text-[clamp(3.25rem,12vw,9.5rem)] font-medium uppercase leading-[0.95] tracking-[0.02em] text-white/6">
          Tycoonex
        </p>
      </div>
    </footer>
  );
}
